import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, ArrowUp, ArrowDown } from '@phosphor-icons/react';

export default function PassbookPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.getPassbook(); setEntries(data); } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const lastBalance = entries.length > 0 ? entries[entries.length - 1].running_balance : 0;
  const totalDeposits = entries.filter(e => e.entry_type === 'deposit').reduce((s, e) => s + e.amount, 0);
  const totalWithdrawals = entries.filter(e => e.entry_type === 'withdrawal').reduce((s, e) => s + e.amount, 0);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>Passbook</h1>
          <p className="text-sm mt-1" style={{ color: '#8A94A6' }}>Track deposits and withdrawals</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="h-9 px-4 text-sm font-semibold rounded-md" style={{ background: '#0A2540', color: 'white' }}>
          <Plus size={16} weight="bold" className="mr-1" /> New Entry
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
          <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Current Balance</p>
          <p className="text-2xl font-bold mt-1" style={{ color: '#0A2540', fontVariantNumeric: 'tabular-nums' }}>₹{lastBalance.toLocaleString()}</p>
        </div>
        <div className="p-4 rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
          <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Total Deposits</p>
          <p className="text-2xl font-bold mt-1" style={{ color: '#34C759', fontVariantNumeric: 'tabular-nums' }}>₹{totalDeposits.toLocaleString()}</p>
        </div>
        <div className="p-4 rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
          <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Total Withdrawals</p>
          <p className="text-2xl font-bold mt-1" style={{ color: '#FF3B30', fontVariantNumeric: 'tabular-nums' }}>₹{totalWithdrawals.toLocaleString()}</p>
        </div>
      </div>

      <div className="rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
        <div className="divide-y" style={{ borderColor: '#E2E4E9' }}>
          {loading ? (
            <div className="px-4 py-8 text-center" style={{ color: '#8A94A6' }}>Loading...</div>
          ) : entries.length === 0 ? (
            <div className="px-4 py-8 text-center" style={{ color: '#8A94A6' }}>No entries yet</div>
          ) : [...entries].reverse().map((e, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors">
              <div className="w-8 h-8 rounded flex items-center justify-center" style={{ background: e.entry_type === 'deposit' ? '#F0FFF4' : '#FFF0F0' }}>
                {e.entry_type === 'deposit' ? <ArrowDown size={16} style={{ color: '#34C759' }} /> : <ArrowUp size={16} style={{ color: '#FF3B30' }} />}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium" style={{ color: '#0A2540' }}>{e.description}</p>
                <p className="text-xs" style={{ color: '#8A94A6' }}>{e.reference} • {e.created_at?.slice(0, 10)}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold" style={{ color: e.entry_type === 'deposit' ? '#34C759' : '#FF3B30', fontVariantNumeric: 'tabular-nums' }}>
                  {e.entry_type === 'deposit' ? '+' : '-'}₹{e.amount.toLocaleString()}
                </p>
                <p className="text-xs" style={{ color: '#8A94A6', fontVariantNumeric: 'tabular-nums' }}>Bal: ₹{e.running_balance.toLocaleString()}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={showForm} onOpenChange={() => setShowForm(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>New Passbook Entry</DialogTitle></DialogHeader>
          <PassbookForm onSave={() => { setShowForm(false); load(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PassbookForm({ onSave }) {
  const [form, setForm] = useState({ entry_type: 'deposit', amount: 0, description: '', reference: '' });
  const save = async () => {
    try { await api.createPassbookEntry(form); onSave(); } catch {}
  };
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Type</Label>
        <Select value={form.entry_type} onValueChange={v => setForm(f => ({ ...f, entry_type: v }))}>
          <SelectTrigger className="h-8 mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="deposit">Deposit</SelectItem>
            <SelectItem value="withdrawal">Withdrawal</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Amount</Label>
        <Input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: Number(e.target.value) }))} className="h-8 mt-1" />
      </div>
      <div>
        <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Description</Label>
        <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="h-8 mt-1" />
      </div>
      <div>
        <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Reference</Label>
        <Input value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} className="h-8 mt-1" />
      </div>
      <Button onClick={save} className="w-full h-8 text-sm" style={{ background: '#0A2540', color: 'white' }}>Save</Button>
    </div>
  );
}
